import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import type { Device } from '@sites/entity-core';
import type { BlockZeroForm } from '@sites/entity-signal-form';

import type { NormalizedZeroBlockEntity } from '@web/types';

import { CSS_RESTRICTIONS } from '@common/constants';

import { SettingsBodyComponent } from '../settings-body/settings-body.component';
import { SettingsBreadcrumbsComponent } from '../settings-breadcrumbs/settings-breadcrumbs.component';
import { WebDeviceToggleComponent } from '@web/shared/components/web-device-toggle/web-device-toggle.component';
import { WebSynchronizeStyleToggleComponent } from '@web/shared/components/web-synchronize-style-toggle';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpInputNumComponent } from '@libs/components/sp-form-controls/sp-input-num/sp-input-num.component';
import { WebBackgroundImageSettingsComponent } from '@web/shared/components/web-background-image-settings/web-background-image-settings.component';
import { WebBackgroundVideoSettingsComponent } from '@web/shared/components/web-background-video-settings/web-background-video-settings.component';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { SpAdvancedBorderSettingsComponent } from '@libs/components/sp-advanced-border-settings/sp-advanced-border-settings.component';
import { BlockLibraryAddonComponent } from '../block-settings/components/block-library-addon/block-library-addon.component';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';

import { AbstractBlockSettingsComponent } from '../abstract-base-settings/abstract-block-settings.component';

/**
 * Settings for a free-positioning canvas block — see {@link AbstractBlockSettingsComponent}.
 * A canvas owns an explicit height rather than padding.
 */
@Component({
    selector: 'web-zero-block-settings',
    templateUrl: './zero-block-settings.component.html',
    styleUrls: ['./zero-block-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SettingsBodyComponent,
        SettingsBreadcrumbsComponent,
        TooltipModule,
        WebDeviceToggleComponent,
        WebSynchronizeStyleToggleComponent,
        WebColorPickerComponent,
        SpInputNumComponent,
        WebBackgroundImageSettingsComponent,
        WebBackgroundVideoSettingsComponent,
        SpSwitchComponent,
        SpAdvancedBorderSettingsComponent,
        BlockLibraryAddonComponent,
        WebCustomIdComponent,
        TranslateModule,
    ],
})
export class ZeroBlockSettingsComponent extends AbstractBlockSettingsComponent<
    BlockZeroForm,
    NormalizedZeroBlockEntity
> {
    public readonly entity = input.required<NormalizedZeroBlockEntity>();

    public readonly HEIGHT_MIN = CSS_RESTRICTIONS.BLOCK_PADDING_MIN;

    public readonly height = computed<number>(() => this.at<{ value: number } | null>(this.form.height)?.value ?? 0);

    protected copyDesktopBox(device: Device): void {
        this.form.setHeight(this.form.height.desktop().value(), device);
    }

    public setHeight(value: number): void {
        // A canvas is exactly this tall; the unit never varies.
        this.write((device) => this.form.setHeight({ value, unit: 'px' }, device));
    }
}
